import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mockCustomers } from '@/mocks/tr/customers';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    'Bekliyor': 'bg-gold-50 text-gold-600 border-gold-200',
    'Kısmi Ödendi': 'bg-blue-50 text-blue-600 border-blue-200',
    'Gecikti': 'bg-red-50 text-red-600 border-red-200',
    'Kapandı': 'bg-green-50 text-green-600 border-green-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'Gecikti' ? 'bg-red-500' : status === 'Kapandı' ? 'bg-green-500' : 'bg-gold-500'}`} />
      {status}
    </span>
  );
}

export default function Credit() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);

  const summary = useMemo(() => {
    const totalCredit = mockCustomers.reduce((sum, c) => sum + c.remaining_debt, 0);
    const overdue = mockCustomers.filter((c) => c.status === 'Gecikti').reduce((sum, c) => sum + c.remaining_debt, 0);
    const thisWeek = mockCustomers.filter((c) => c.status === 'Bekliyor').reduce((sum, c) => sum + c.remaining_debt, 0);
    const closed = mockCustomers.filter((c) => c.status === 'Kapandı').length;
    return { totalCredit, overdue, thisWeek, closed };
  }, []);

  const filtered = useMemo(() => {
    return mockCustomers.filter((c) => {
      const matchSearch =
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.phone.includes(search);
      const matchStatus = !filterStatus || c.status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [search, filterStatus]);

  const customer = mockCustomers.find((c) => c.id === selectedCustomer);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black">{t('credit.title')}</h1>
          <p className="text-sm text-boutique-warm mt-1">Müşteri borçlarını takip edin ve tahsilat yapın</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gold-50 flex items-center justify-center text-gold-500">
              <i className="ri-time-line text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('credit.totalCredit')}</p>
              <p className="text-xl font-semibold text-boutique-black">{formatCurrency(summary.totalCredit)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-500">
              <i className="ri-alarm-warning-line text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('credit.overdue')}</p>
              <p className="text-xl font-semibold text-red-600">{formatCurrency(summary.overdue)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
              <i className="ri-calendar-check-line text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('credit.thisWeek')}</p>
              <p className="text-xl font-semibold text-blue-600">{formatCurrency(summary.thisWeek)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-500">
              <i className="ri-check-double-line text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">{t('credit.closed')}</p>
              <p className="text-xl font-semibold text-green-600">{summary.closed} Müşteri</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 bg-white p-4 rounded-xl border border-cream-200">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-boutique-warm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm bg-white sm:w-48"
        >
          <option value="">{t('common.all')}</option>
          <option value="Bekliyor">{t('common.pending')}</option>
          <option value="Kısmi Ödendi">{t('common.partial')}</option>
          <option value="Gecikti">{t('common.overdue')}</option>
          <option value="Kapandı">{t('common.closed')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-cream-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50 border-b border-cream-200">
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Müşteri</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Telefon</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Toplam Alışveriş</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Ödenen</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Kalan Borç</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Son Ödeme</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Durum</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-100">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-cream-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-boutique-black">{c.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{c.phone}</td>
                  <td className="px-4 py-3 text-right font-medium text-boutique-black whitespace-nowrap">{formatCurrency(c.total_purchases)}</td>
                  <td className="px-4 py-3 text-right text-green-600 font-medium whitespace-nowrap">{formatCurrency(c.total_paid)}</td>
                  <td className="px-4 py-3 text-right text-red-600 font-semibold whitespace-nowrap">{formatCurrency(c.remaining_debt)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{c.last_payment_date}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {c.remaining_debt > 0 && (
                        <button
                          onClick={() => { setSelectedCustomer(c.id); setShowPaymentModal(true); }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-green-600 hover:bg-green-50 transition-colors"
                          title={t('common.payment')}
                        >
                          <i className="ri-money-cny-circle-line text-base" />
                        </button>
                      )}
                      <a
                        href={`https://wa.me/${c.phone.replace(/\s/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-green-500 hover:bg-green-50 transition-colors"
                        title={t('common.whatsapp')}
                      >
                        <i className="ri-whatsapp-line text-base" />
                      </a>
                      <button
                        onClick={() => { setSelectedCustomer(c.id); setShowDetailModal(true); }}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors"
                        title={t('common.detail')}
                      >
                        <i className="ri-eye-line text-base" />
                      </button>
                      {c.remaining_debt > 0 && (
                        <button
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-gold-600 hover:bg-gold-50 transition-colors"
                          title={t('common.closeDebt')}
                        >
                          <i className="ri-check-double-line text-base" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-time-line text-4xl text-cream-300 mb-3" />
            <p className="text-sm text-boutique-warm">{t('common.noData')}</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && customer && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-cream-200 flex items-center justify-between">
              <h2 className="font-semibold text-boutique-black text-lg">{t('credit.customerDetail')}</h2>
              <button onClick={() => setShowDetailModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gold-100 flex items-center justify-center text-gold-600 font-semibold text-xl">
                  {customer.name.charAt(0)}
                </div>
                <div>
                  <h3 className="font-semibold text-boutique-black text-lg">{customer.name}</h3>
                  <p className="text-sm text-boutique-warm">{customer.phone}</p>
                  <div className="mt-1">
                    <StatusBadge status={customer.status} />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-cream-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-boutique-warm">Toplam Alışveriş</p>
                  <p className="font-semibold text-boutique-black">{formatCurrency(customer.total_purchases)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600">Ödenen</p>
                  <p className="font-semibold text-green-600">{formatCurrency(customer.total_paid)}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-red-600">Kalan Borç</p>
                  <p className="font-semibold text-red-600">{formatCurrency(customer.remaining_debt)}</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-boutique-black mb-3">{t('credit.saleHistory')}</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-cream-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-boutique-black">Deri ceket</p>
                      <p className="text-xs text-boutique-warm">01.06.2026</p>
                    </div>
                    <p className="font-medium text-boutique-black">{formatCurrency(4500)}</p>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-cream-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-boutique-black">İpek elbise</p>
                      <p className="text-xs text-boutique-warm">28.05.2026</p>
                    </div>
                    <p className="font-medium text-boutique-black">{formatCurrency(3200)}</p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-sm text-boutique-black mb-3">{t('credit.paymentHistory')}</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-green-700">Nakit Ödeme</p>
                      <p className="text-xs text-green-600">01.06.2026</p>
                    </div>
                    <p className="font-medium text-green-700">{formatCurrency(1200)}</p>
                  </div>
                </div>
              </div>
              {customer.notes && (
                <div>
                  <h4 className="font-semibold text-sm text-boutique-black mb-2">{t('common.notes')}</h4>
                  <p className="text-sm text-boutique-warm bg-cream-50 p-3 rounded-lg">{customer.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && customer && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-cream-200 flex items-center justify-between">
              <h2 className="font-semibold text-boutique-black text-lg">{t('common.payment')}</h2>
              <button onClick={() => setShowPaymentModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-cream-50 rounded-lg p-4">
                <p className="text-sm text-boutique-warm">Müşteri</p>
                <p className="font-medium text-boutique-black">{customer.name}</p>
                <p className="text-sm text-red-600 font-medium mt-1">Kalan: {formatCurrency(customer.remaining_debt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tutarı</label>
                <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tarihi</label>
                <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Tipi</label>
                <select className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm bg-white">
                  <option>Nakit</option>
                  <option>Kart</option>
                  <option>Havale</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowPaymentModal(false)} className="flex-1 py-2.5 px-4 border border-cream-200 text-boutique-black rounded-lg text-sm font-medium hover:bg-cream-50 transition-colors">
                  {t('common.cancel')}
                </button>
                <button className="flex-1 py-2.5 px-4 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}